import { useState, useEffect, useCallback, useRef } from 'react';
import { OrchestratorService } from '../services/OrchestratorService';
import type { OrchestratorRow } from '../types/orchestrator';
import type { PaginationInfo } from '../types/student';

const SEARCH_DEBOUNCE_MS = 400;

/* ── Hook ────────────────────────────────────────────────── */

export interface OrchestratorFilters {
  searchName?: string;
  filterPaymentStatus?: string[];
  filterPaymentMethod?: string[];
  filterWorkoutStatus?: string[];
  filterFupStatus?: string[];
  filterFupDate?: string;
  filterCoachIds?: string[];
  filterGateway?: string[];
  filterStudentTenure?: string;
  classifications?: string[];
  customDateStart?: string;
  customDateEnd?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  lastWorkoutDateStart?: string;
  lastWorkoutDateEnd?: string;
  projectCodes?: string[];
  sortOrder?: string;
}

export interface UseOrchestratorReturn {
  rows: OrchestratorRow[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOrchestratorData(
  page: number = 1,
  pageSize: number = 10,
  filters: OrchestratorFilters = {},
): UseOrchestratorReturn {
  const [rows, setRows] = useState<OrchestratorRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (requestedPage: number, requestedPageSize: number, serverFilters: OrchestratorFilters) => {
      const currentReq = ++reqId.current;
      try {
        setLoading(true);
        setError(null);

        const response = await OrchestratorService.listPaginated(
          requestedPage,
          requestedPageSize,
          serverFilters,
        );

        if (currentReq !== reqId.current) return;

        setRows(response.data);
        setPagination(response.pagination);
      } catch (err: any) {
        if (currentReq !== reqId.current) return;
        console.error('[useOrchestratorData]', err);
        setError(err.message || 'Erro ao carregar dados do orquestrador');
      } finally {
        if (currentReq === reqId.current) setLoading(false);
      }
    },
    [],
  );

  // Debounce search, immediate for other filters
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousRequest = useRef<{
    searchName: string | undefined;
    immediateFiltersKey: string;
    pageSize: number;
  } | null>(null);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    const currentFilters = JSON.parse(filtersKey) as OrchestratorFilters;
    const {
      searchName,
      ...immediateFilters
    } = currentFilters;
    const immediateFiltersKey = JSON.stringify(immediateFilters);
    const previous = previousRequest.current;
    const onlySearchChanged = previous !== null
      && searchName !== previous.searchName
      && immediateFiltersKey === previous.immediateFiltersKey
      && pageSize === previous.pageSize;

    previousRequest.current = {
      searchName,
      immediateFiltersKey,
      pageSize,
    };

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, currentFilters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(page, pageSize, currentFilters);
    }

    return () => clearTimeout(debounceTimer.current);
  }, [filtersKey, fetchPage, page, pageSize]);

  const refetch = useCallback(() => {
    fetchPage(page, pageSize, filters);
  }, [fetchPage, filters, page, pageSize]);

  return {
    rows,
    pagination: {
      ...pagination,
      page,
      pageSize,
    },
    loading,
    error,
    refetch,
  };
}
